/**
 * CaptureGapsTab — questions asked mid-conversation that the current board
 * data couldn't answer (e.g. "how many times did #25 slip?"), surfaced so
 * an admin can go add the missing source/property to Notion.
 *
 * Status filter (Open / Addressed) + a dense table. Marking a gap addressed
 * is admin-only; the backend enforces the same gate.
 */

import {
  Alert,
  Box,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState, type ReactElement } from 'react';

import { ApiError, memory } from '@/helpers/ApiHelper';
import type { CaptureGapOut, CaptureGapStatus } from '@/types/memory';

const dateFmt = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' });

export interface CaptureGapsTabProps {
  /** When false, "Mark addressed" is hidden. Backend enforces the same gate. */
  canEdit: boolean;
}

export function CaptureGapsTab({ canEdit }: CaptureGapsTabProps): ReactElement {
  const [status, setStatus] = useState<CaptureGapStatus>('open');
  const [gaps, setGaps] = useState<CaptureGapOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await memory.listCaptureGaps({ status });
      setGaps(data.results);
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Failed to load capture gaps',
      );
    }
  }, [status]);

  useEffect(() => {
    setGaps(null);
    void refresh();
  }, [refresh]);

  const onMarkAddressed = useCallback(
    async (gap: CaptureGapOut) => {
      setBusyId(gap.id);
      // Optimistic: drop the row immediately, then reconcile with the server.
      setGaps((prev) => (prev ? prev.filter((g) => g.id !== gap.id) : prev));
      let failure: string | null = null;
      try {
        await memory.setCaptureGapStatus(gap.id, 'addressed');
      } catch (e) {
        failure = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Failed to mark addressed';
      } finally {
        setBusyId(null);
        // refresh() clears `error` on success — restore the failure message
        // (if any) after it, so a failed POST doesn't get silently erased by
        // its own follow-up refetch.
        await refresh();
        if (failure) setError(failure);
      }
    },
    [refresh],
  );

  return (
    <Box>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={status}
        onChange={(_, v: CaptureGapStatus | null) => {
          if (v !== null) setStatus(v);
        }}
        aria-label="Capture gap status"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="open" sx={{ minHeight: 44 }}>
          Open
        </ToggleButton>
        <ToggleButton value="addressed" sx={{ minHeight: 44 }}>
          Addressed
        </ToggleButton>
      </ToggleButtonGroup>

      {error && (
        <Alert
          severity="error"
          role="alert"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" sx={{ minHeight: 44 }} onClick={() => void refresh()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!error && gaps == null && (
        <Box role="status" aria-label="Loading capture gaps">
          <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={40} />
        </Box>
      )}

      {gaps != null && gaps.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {status === 'open'
            ? 'No open capture gaps — every question this week had an answer on the board.'
            : 'No addressed capture gaps yet.'}
        </Typography>
      )}

      {gaps != null && gaps.length > 0 && (
        <TableContainer>
          <Table size="small" aria-label="Capture gaps">
            <TableHead>
              <TableRow>
                <TableCell>Question</TableCell>
                <TableCell>What&apos;s missing</TableCell>
                <TableCell>Where it would come from</TableCell>
                <TableCell>Asked by</TableCell>
                <TableCell>When</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {gaps.map((gap) => (
                <TableRow key={gap.id} hover>
                  <TableCell sx={{ verticalAlign: 'top', maxWidth: 320 }}>
                    <Typography variant="body2">{gap.question}</Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography variant="body2">{gap.missing}</Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Typography variant="body2" color="text.secondary">
                      {gap.suggested_source}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    <Typography variant="body2">{gap.asked_by}</Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      {dateFmt.format(new Date(gap.created_at))}
                    </Typography>
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      {gap.status === 'open' && (
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ minHeight: 44 }}
                          disabled={busyId === gap.id}
                          onClick={() => void onMarkAddressed(gap)}
                        >
                          Mark addressed
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
